import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, Divider, Group, Stack, Text } from '@mantine/core';
import { IconArrowRight, IconFlask, IconScale } from '@tabler/icons-react';
import BestPriceLabel, { type BestPriceAction } from '@/components/common/BestPriceLabel';
import MetaChip from '@/components/common/MetaChip';
import PrepaymentBadge from '@/components/common/PrepaymentBadge';
import NoNds from '@/components/common/NoNds';
import { formatMoney, formatProperty } from '@/lib/format';
import type { GoodsProperty } from '@/types/buy';

interface DealSummaryCardProps {
  /** Культура + класс, напр. «Пшеница 4 класс». */
  readonly goods: string;
  /** Главный параметр качества (протеин и т.п.) — чипом; необязателен. */
  readonly quality?: GoodsProperty;
  /** Объём сделки, т (для продажи — спрос покупателя, для покупки — из оффера продавца). */
  readonly weightTon?: number;
  readonly action: BestPriceAction;
  /** Лучшая (встречная) цена — герой, оранжевая. */
  readonly bestPrice?: number;
  /** Подпись над лучшей ценой, напр. «Вы получаете (FCA, у ворот)» / «Цена с доставкой (CPT)». */
  readonly bestPriceLabel: string;
  /** Моя цена (ориентир) — база слева; undefined в свободном режиме (её нет). */
  readonly myPrice?: number;
  /** Выгода относительно моей цены (для дельты). */
  readonly advantage?: number;
  readonly prepaymentValue?: string | number | null;
  readonly total?: number;
  readonly totalLabel: string;
  readonly distance?: number;
  readonly distanceLabel: string;
  /** Доп. строка итогов, напр. «Покупатель платит (CPT) X ₽/т» (только продажа). */
  readonly extra?: ReactNode;
}

// Сводка сделки в первой секции превью (Вариант A): что/качество/тоннаж → контраст «моя цена → лучшая»
// с выигрышем → итоги (кол-во × цена + логистика). Единый вид для продажи и покупки; лучшая цена — герой
// (оранжевая через BestPriceLabel static), моя цена — тихий серый ориентир, дельта — зелёный/красный.
export default function DealSummaryCard({
  goods,
  quality,
  weightTon,
  action,
  bestPrice,
  bestPriceLabel,
  myPrice,
  advantage,
  prepaymentValue,
  total,
  totalLabel,
  distance,
  distanceLabel,
  extra,
}: DealSummaryCardProps) {
  const { t } = useTranslation();
  // При выгоде ≈0 моя цена совпадает с лучшей — вторую (одинаковую) колонку и стрелку не показываем,
  // чтобы не дублировать одно и то же число. В свободном режиме myPrice отсутствует — колонки тоже нет.
  const showMyPrice = myPrice != null && Math.round(advantage ?? Number.NaN) !== 0;

  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        {/* Что / качество / сколько. Иконку колоса не дублируем — она уже в заголовке страницы над картой. */}
        <Group gap="sm" wrap="wrap" align="center">
          <Text fw={600} c="brand.9">
            {goods}
          </Text>
          {quality ? <MetaChip icon={<IconFlask size={13} />}>{formatProperty(quality)}</MetaChip> : null}
          {weightTon != null ? <MetaChip icon={<IconScale size={13} />}>{formatMoney(weightTon)} т</MetaChip> : null}
        </Group>

        <Divider />

        {/* Контраст: моя цена → лучшая цена + выигрыш */}
        <Group gap="lg" wrap="wrap" align="flex-start">
          {showMyPrice ? (
            <Stack gap={2}>
              <Text size="sm" c="gray.7">
                {t('app.bestPrice.yourPrice')}
              </Text>
              <Group gap={6} align="baseline" wrap="nowrap">
                <Text fz={22} fw={600} c="gray.7">
                  {formatMoney(myPrice)}
                </Text>
                <Text size="sm" c="gray.6">
                  ₽/т
                </Text>
              </Group>
            </Stack>
          ) : null}

          {showMyPrice ? (
            <Box visibleFrom="sm" style={{ alignSelf: 'center' }}>
              <IconArrowRight size={20} color="var(--mantine-color-gray-5)" />
            </Box>
          ) : null}

          <Stack gap={6} align="flex-start">
            <Text size="sm" c="gray.8">
              {bestPriceLabel}
            </Text>
            {bestPrice != null ? (
              <BestPriceLabel size="lg" price={bestPrice} action={action} advantage={advantage} />
            ) : (
              <Text fz={28} fw={600} c="brand.9">
                —
              </Text>
            )}
            <PrepaymentBadge value={prepaymentValue} />
          </Stack>
        </Group>

        <Divider />

        {/* Итоги: сумма = кол-во × цена, доп. строка, расстояние */}
        <Stack gap={6}>
          <Group gap={8} wrap="wrap" align="baseline">
            <Text size="sm" c="gray.8">
              {totalLabel}:
            </Text>
            <Text fw={600}>
              {total != null ? (
                <>
                  {formatMoney(total)} ₽<NoNds />
                </>
              ) : (
                '—'
              )}
            </Text>
            {total != null && weightTon != null && bestPrice != null ? (
              <Text size="sm" c="gray.6">
                · {formatMoney(weightTon)} т × {formatMoney(bestPrice)}
              </Text>
            ) : null}
          </Group>
          {extra || distance != null ? (
            <Group gap="lg" wrap="wrap">
              {extra}
              {distance != null ? (
                <Group gap={6} wrap="nowrap">
                  <Text size="sm" c="gray.8">
                    {distanceLabel}:
                  </Text>
                  <Text fw={600}>{formatMoney(distance)} км</Text>
                </Group>
              ) : null}
            </Group>
          ) : null}
        </Stack>
      </Stack>
    </Card>
  );
}
